import React, { createContext, useState, useContext, useEffect } from 'react';
import { useCurrentUser } from '../hooks/useUserQueries';
import { useLogout } from '../hooks/useAuthQueries';
import { tokenUtils } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const hasTokens = tokenUtils.hasTokens();
  const { data: currentUser, isLoading: isLoadingUser, error: currentUserError } = useCurrentUser({
    enabled: hasTokens,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  
  const logoutMutation = useLogout();

  // Check for existing tokens and user data on app load
  useEffect(() => {
    const userData = localStorage.getItem('user');
    
    if (hasTokens && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing user data:', error);
        tokenUtils.clearTokens();
      }
    } else if (!hasTokens) {
      // Clear user state if no tokens
      setUser(null);
      setIsAuthenticated(false);
    }
    
    setLoading(false);
  }, [hasTokens]);

  // Update user when currentUser query succeeds
  useEffect(() => {
    if (currentUser) {
      setUser(currentUser);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(currentUser));
    }
  }, [currentUser]);

  // Handle authentication errors (401/403) - clear user state
  useEffect(() => {
    if (currentUserError && (currentUserError.status === 401 || currentUserError.status === 403)) {
      // Token is invalid or expired - clear user state
      // Note: Token refresh is handled automatically in api.js
      // If refresh also fails, tokens will be cleared there
      if (!tokenUtils.hasTokens()) {
        setUser(null);
        setIsAuthenticated(false);
      }
    }
  }, [currentUserError]);

  // Handle loading state
  useEffect(() => {
    if (hasTokens) {
      setLoading(isLoadingUser);
    } else {
      setLoading(false);
    }
  }, [hasTokens, isLoadingUser]);

  const login = (userData, accessToken, refreshToken) => {
    setUser(userData);
    setIsAuthenticated(true);
    tokenUtils.setTokens(accessToken, refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  const value = {
    user,
    isAuthenticated,
    loading: loading || logoutMutation.isPending,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
